import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { ProductSet, ProductSetForm, ProductSetSearchForm } from 'entities/product_set'

type ProductSetsResponse = PagingResponse & {
    results: ProductSet[]
}

export function useFetchProductSetsApi(searchForm?: Form<ProductSetSearchForm>): IndexApiSet<ProductSetsResponse> & { execute: () => void } {
    const apiPath = 'product_sets/'
    const api = useIndexApi<ProductSetsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchProductSetApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `product_sets/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostProductSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProductSetForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ProductSetForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<ProductSetForm>) => {
        const apiPath = `product_sets/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutProductSetApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProductSetForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ProductSetForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<ProductSetForm>) => {
        const apiPath = `product_sets/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutProductSetsApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProductSetForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ProductSetForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProductSetForm[]>) => {
        const apiPath = `product_sets/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteProductSetApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `product_sets/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

